import { EntityType, MarketType } from 'api'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { isProductSectorBasketTypeGCGetterSelector, productByProductSectorIdGetterSelector } from 'selectors/productSelectors'
import { isTradingGatewayUserSelector } from 'selectors/userSelectors'
import { isNotNil } from 'utils/predicate'
import {
  authorisedUserIsPermissionedToTradeInstrumentSelector,
  createInstrumentCanBeTradedByTradingEntitySelector,
  createInstrumentHasSameMicCodeSelector,
  createTradingEntityIsPermissionedToTradeInstrumentSelector,
  EMPTY_INSTRUMENT_SET_FILTER,
  instrumentIsPermittedToBeSecondarySblSelector
} from './instrumentFilterSelectors'

export interface SecondaryCollateralSelectorProps {
  collateralBasketId?: string | null
  collateralId?: string | null
  fundId?: string | null
  counterpartyIds?: string[]
  marketType: MarketType | null
}

export const createInstrumentFiltersForSecondaryCollateralSelector = <T extends SecondaryCollateralSelectorProps, P>(
  propsSelector: Selector<{}, T, [P]>
) => {
  return createSelector(
    [
      propsSelector,
      createEntityGetterSelector(EntityType.COLLATERAL),
      createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
      productByProductSectorIdGetterSelector,
      isTradingGatewayUserSelector,
      isProductSectorBasketTypeGCGetterSelector,
      createInstrumentCanBeTradedByTradingEntitySelector,
      authorisedUserIsPermissionedToTradeInstrumentSelector,
      createInstrumentHasSameMicCodeSelector,
      createTradingEntityIsPermissionedToTradeInstrumentSelector,
      instrumentIsPermittedToBeSecondarySblSelector
    ],
    (
      props,
      getCollateral,
      getBasket,
      getProduct,
      isTradingUser,
      isProductSectorBasketTypeGC,
      createInstrumentCanBeTradedByTradingEntities,
      authorisedUserIsPermissionedToTradeInstrument,
      createInstrumentHasSameMicCode,
      tradingEntityIsPermissionedToTradeInstrument,
      instrumentIsPermittedToBeSecondarySbl
    ) => {
      const { collateralBasketId, collateralId, fundId, counterpartyIds = [], marketType } = props

      const collateral = getCollateral(collateralId)
      const basket = getBasket(collateralBasketId)

      const primaryCollateralIsNonBasket = isNotNil(collateral)
      const cbProductSectorId = basket?.productSectorId
      const primaryCollateralIsGC = isNotNil(cbProductSectorId) && isProductSectorBasketTypeGC(cbProductSectorId)
      const fundIds = fundId ? [fundId] : []

      if (!primaryCollateralIsNonBasket && !primaryCollateralIsGC) {
        return [EMPTY_INSTRUMENT_SET_FILTER]
      }

      const primaryProduct = getProduct(collateral?.productSectorId ?? basket?.productSectorId)

      return [
        instrumentIsPermittedToBeSecondarySbl,
        createInstrumentCanBeTradedByTradingEntities(marketType, [...fundIds, ...counterpartyIds]),
        ...(isTradingUser ? [authorisedUserIsPermissionedToTradeInstrument] : []),
        tradingEntityIsPermissionedToTradeInstrument([...fundIds, ...counterpartyIds]),
        ...(primaryProduct ? [createInstrumentHasSameMicCode(primaryProduct, marketType)] : [])
      ]
    }
  )
}
